import { useSessionIdContext } from "../context/SessionIdContext";
import { useUrlParams } from "../hooks/useUrlParams";

import { JSX } from "react";
import { useTranslation } from "react-i18next";

import {
  Alert,
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
} from "@mui/material";
import {
  CommonErrorBoundary,
  DateLocalizationProvider,
  LocalizationProvider,
  OAuthManager,
} from "@plansoft-configuration-renderer/components";
import {
  GatewayDest,
  GatewayProvider,
} from "@plansoft-configuration-renderer/gateway";

import { useSession } from "@/renderer/hooks/useSession";

import { MinimalAppContext } from "./MinimalAppContext";
import { MinimalRenderer } from "./MinimalRenderer";

export function MinimalApp(): JSX.Element {
  const cfgObjects = [
    "GW_MAIN",
    "BS_MAIN",
    "HX_MAIN",
    "KK_MAIN",
    "RT_MAIN",
    "SP_MAIN",
    "MOTOR_CFG",
    "MOTOR_MAIN",
    "EA_MAIN",
    "DT_MAIN",
    "LMSSA_MAIN",
  ];
  const { params } = useUrlParams();

  const { loadedSessionIdParam } = useSessionIdContext();
  const {
    sessionId,
    language,
    error: sessionParamsError,
    isLoading: isLoadingSessionParams,
  } = useSession(params, loadedSessionIdParam);
  const { t } = useTranslation();

  const activeCfgObjectId = window.location.hash.split("/")[1];

  const activeLanguage =
    language || import.meta.env.VITE_APP_LANGUAGE || navigator.language;
  const activeUiCulture = activeLanguage;

  if (isLoadingSessionParams) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center">
        <CircularProgress size={100} />
      </Box>
    );
  }

  if (sessionParamsError) {
    return (
      <Alert severity="error">{t(`error.${sessionParamsError.message}`)}</Alert>
    );
  }

  return (
    <MinimalAppContext>
      <LocalizationProvider
        configurationLocale={activeLanguage}
        uiLocale={activeUiCulture}
        configuratorBasePath={import.meta.env.VITE_CONFIGURATOR_BASE_URL}
      >
        <DateLocalizationProvider localeCode={activeUiCulture}>
          <GatewayProvider>
            <CommonErrorBoundary>
              {(sessionId ?? activeCfgObjectId) ? (
                <>
                  <MinimalRenderer
                    sessionId={sessionId}
                    baseUrl={import.meta.env.VITE_CONFIGURATOR_BASE_URL}
                    cfgObjectId={activeCfgObjectId}
                    language={activeLanguage}
                  />
                </>
              ) : import.meta.env.DEV ? (
                <MinimalRenderer
                  baseUrl={import.meta.env.VITE_CONFIGURATOR_BASE_URL}
                  cfgObjectId={import.meta.env.VITE_APP_CFG_OBJECT_ID}
                  language={activeLanguage}
                />
              ) : (
                <Box>
                  <Alert severity="error">
                    <Box marginBottom={"0.75rem"}>
                      No cfgObjectId set. Please set an cfgObject or select the
                      cfgObject you were looking for. When including this
                      application in an iFrame, check the url of the iFrame for
                      the correct cfgObjectId.
                    </Box>
                    <Select
                      defaultValue=""
                      sx={{ minWidth: "200px" }}
                      onChange={(event) => {
                        window.location.href = "/#/" + event.target.value;
                        window.location.reload();
                      }}
                    >
                      {cfgObjects.map((cfgObject) => {
                        return (
                          <MenuItem value={cfgObject} key={cfgObject}>
                            {cfgObject}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </Alert>
                </Box>
              )}
            </CommonErrorBoundary>
            <OAuthManager
              basePath={import.meta.env.VITE_CONFIGURATOR_BASE_URL}
              isLocalDevelopment={import.meta.env.DEV}
            />
            <Grid>
              <Box position="sticky" top={0}>
                <GatewayDest name="navigation" />
              </Box>
            </Grid>
          </GatewayProvider>
        </DateLocalizationProvider>
      </LocalizationProvider>
    </MinimalAppContext>
  );
}
